import { create } from 'apisauce';

import api, { apiSetup, HEADERS } from '~config/api';
import { mapCart } from '~redux/Cart/mappers';
import { retry } from '~utils/functions';
import { API_ERROR_CODES } from '~constants/apiErrors';
import LocalStorageService from '~services/LocalStorageService';

import { mapResponse } from './mappers';

export const COUPON_CODE = {
  COUPON_EXPIRED: 'COUPON_EXPIRED',
  COUPON_MAX_REACHED: 'COUPON_MAX_REACHED',
  COUPON_NOT_AVAILABLE: 'COUPON_NOT_AVAILABLE',
  MAX_COUPON_USES_BY_USER_REACHED: 'MAX_COUPON_USES_BY_USER_REACHED',
  COUPON_DISPATCH_METHOD: 'COUPON_DISPATCH_METHOD',
  COUPON_CANNOT_BE_USED_BY_USER: 'COUPON_CANNOT_BE_USED_BY_USER',
  COUPON_WITH_MINIMUM_BILL: 'COUPON_WITH_MINIMUM_BILL',
  COUPON_PLATFORM_ERROR: 'COUPON_PLATFORM_ERROR',
  NOT_FIRST_PURCHASE: 'NOT_FIRST_PURCHASE',
  INVALID_INVITATION_CODE: 'INVALID_INVITATION_CODE',
  INVALID_MINIMUM_START_TIME: 'INVALID_MINIMUM_START_TIME',
  INVALID_MAXIMUM_EXPIRY_TIME: 'INVALID_MAXIMUM_EXPIRY_TIME',
  COUPON_COUPONIZABLE_ERROR: 'COUPON_COUPONIZABLE_ERROR',
  INVALID_COUPON_CODE: 'INVALID_COUPON_CODE',
  COUPON_DISABLED: 'COUPON_DISABLED',
  MISSING_PAYMENT_METHOD_FOR_COUPON: 'MISSING_PAYMENT_METHOD_FOR_COUPON',
  INVALID_PAYMENT_METHOD_FOR_COUPON: 'INVALID_PAYMENT_METHOD_FOR_COUPON',
  INVALID_STORE_ID: 'INVALID_STORE_ID'
};

const createTemporalCartApi = (externalId) => {
  const temporalApi = create({
    baseURL: process.env.REACT_APP_BASE_URL,
    headers: {
      ...(externalId && {
        [HEADERS.USER_ID]: externalId
      }),
      [HEADERS.TENANT]: api.headers[HEADERS.TENANT],
      [HEADERS.PLATFORM]: api.headers[HEADERS.PLATFORM]
    }
  });
  apiSetup(temporalApi);
  return temporalApi;
};

const requestShoppingCartsPricesInternal = (temporalCartApi, { withoutCoupon, ...state }) => {
  return temporalCartApi.post('v1/shopping_carts/prices', mapCart({ ...state, withoutCoupon }));
};

export const sendCart = (state) => {
  const temporalCartApi = createTemporalCartApi(state.state.auth?.currentUser?.externalId);

  const requestShoppingCartsPricesWithoutCoupon = () =>
    requestShoppingCartsPricesInternal(temporalCartApi, {
      ...state,
      withoutCoupon: true
    }).then((res) => {
      return mapResponse(res, state);
    });

  const requestShoppingCartsPrices = () =>
    requestShoppingCartsPricesInternal(temporalCartApi, state).then((res) => {
      if (!res.ok) {
        const errorCode = API_ERROR_CODES[`e${res.data.code}`];
        const couponError = COUPON_CODE?.[errorCode];

        if (couponError) {
          LocalStorageService.removeCoupon();
          return requestShoppingCartsPricesWithoutCoupon();
        }
      }

      return mapResponse(res, state);
    });

  return retry(requestShoppingCartsPrices);
};

export const createCart = (cart) => {
  return api.post('v1/shopping_carts', cart);
};

export const getLastCart = () => {
  return api.get('v1/shopping_carts/last');
};

export const updateCart = (cart) => {
  return api.patch(`v1/shopping_carts/${cart.shoppingCart.id}`, cart);
};

export const getCartById = (cartId) => {
  return api.get(`v1/shopping_carts/${cartId}`);
};
